import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DeviceMobile, X } from "@phosphor-icons/react";

const DISMISS_KEY = "sc_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * InstallAppButton — catches the browser's `beforeinstallprompt` event and
 * exposes a small "INSTALL APP" pill in the header. Once the user installs
 * (or dismisses), the pill hides for 7 days. Falls back gracefully on
 * browsers that don't support the API (Safari iOS — user gets iOS "Add to
 * Home Screen" from the share menu, see InstallHint for that).
 */
export default function InstallAppButton({ compact = false }) {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed? standalone display-mode returns true.
    const already = window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator?.standalone === true;
    if (already) return;

    // Dismissed recently?
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setDeferred(null);
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (choice.outcome !== "accepted") {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  if (compact) {
    return (
      <Button
        onClick={install}
        variant="outline"
        size="icon"
        className="rounded-sm h-10 w-10 border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10"
        data-testid="install-app-btn-compact"
        title="Install as app on your phone"
      >
        <DeviceMobile size={18} weight="fill" />
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1" data-testid="install-app-widget">
      <Button
        onClick={install}
        variant="outline"
        className="rounded-sm h-10 px-3 border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10 font-heading tracking-widest text-xs"
        data-testid="install-app-btn"
      >
        <DeviceMobile size={16} weight="fill" className="mr-1" />
        INSTALL APP
      </Button>
      <button
        onClick={dismiss}
        className="text-slate-500 hover:text-slate-300 p-1"
        title="Not now"
        data-testid="install-app-dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
}
